import React from 'react';
import Joi from 'joi';
import getConfig from 'next/config';
import _ from 'underscore';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import { filterQuery } from '@culture/common-helpers/url';

import {
	getBlockFieldsCatalogPersons,
	getCompleteBlocksParamsCatalogPersons
} from 'utils/helpers/blocks';

import Template from 'pageTemplates/persons';
import { findRubricInTreeByName } from 'utils/helpers/rubrics';

const PersonsCatalogPage = (props) => <Template {...props} />;

const { serverRuntimeConfig } = getConfig();
const rubricPath = 'traditions/persons';
const rootRubricId = serverRuntimeConfig.entities.traditionsPersonsRubric;

const rubricBlocksParams = [
	{
		blockName: 'articles',
		isHide: true
	},
	{
		blockName: 'cultureHeritageObjects',
		isHide: true
	},
	{
		blockName: 'cultureHouses',
		isHide: true
	},
	{
		blockName: 'foreignHeritageObjects',
		isHide: true
	},
	{
		blockName: 'incutGridPromoBlock',
		isHide: true
	},
	{
		blockName: 'incutSimplePromoBlock',
		isHide: true
	},
	{
		blockName: 'persons'
	},
	{
		blockName: 'religionHeritageObjects',
		isHide: true
	},
	{
		blockName: 'themes',
		isHide: true
	}
];

const getRubricPage = (cleanCache) =>
	apiRequest(
		'rubricPages/page',
		addCleanCache(
			{
				rubricPath,
				blockFields: getBlockFieldsCatalogPersons(rubricBlocksParams),
				completeBlocksParams: getCompleteBlocksParamsCatalogPersons(
					rubricBlocksParams,
					rootRubricId
				)
			},
			cleanCache
		)
	);

const getPersons = (params, rubricId) => {
	const personsParams = filterQuery({
		..._(params).pick('limit', 'page', 'query'),
		cached: true,
		statuses: ['published'],
		sort: params.sort,
		rubrics: [rubricId],
		fields: [
			'rubrics',
			'title',
			'thumbnailFile',
			'country',
			'type',
			'name'
		].join(),
		limit: 24
	});

	return apiRequest('persons', addCleanCache(personsParams, params.cleanCache));
};

const getPersonsMeta = (urlParams) =>
	apiRequest(
		'meta/catalog',
		addCleanCache(
			{
				..._(urlParams).pick('author', 'tag', 'page'),
				rubricPath,
				cached: true
			},
			urlParams.cleanCache
		)
	);

const getBreadcrumbs = ({ cleanCache }) => {
	const params = addCleanCache(
		{
			rubricPath,
			cached: true
		},
		cleanCache
	);

	return apiRequest('breadcrumbs', params);
};

const getRubricsTree = (cleanCache) =>
	apiRequest(
		'rubrics/tree',
		addCleanCache(
			{
				rootPath: rubricPath,
				entityType: 'persons',
				isExtendFirst: true
			},
			cleanCache
		)
	);

export async function getServerSideProps({ query }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false),
		limit: Joi.number().default(24).min(1),
		query: Joi.string(),
		page: Joi.number().default(1).min(1),
		rubric: Joi.array().items(Joi.string()),
		sort: Joi.string()
			.valid('views', '-views', 'surname', '-surname')
			.failover('-views')
			.default('-views')
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const rubricName = !_.isEmpty(params?.rubric) ? params?.rubric[0] : '';
	const rubricsTree = await getRubricsTree(params.cleanCache);

	const currentRubric = findRubricInTreeByName(rubricsTree, rubricName);
	const currentRubricId = currentRubric?._id || rootRubricId;

	if (_.isEmpty(currentRubric)) {
		delete params.rubric;
	}

	const requests = [
		getBreadcrumbs(params),
		getPersons(params, currentRubricId),
		getPersonsMeta(params, rubricPath),
		getRubricPage(params.cleanCache)
	];
	const [breadcrumbs, persons, meta, rubricPage] = await batchApiRequest(
		requests
	);

	return {
		props: {
			persons,
			breadcrumbs,
			urlParams: _(params).omit('limit'),
			rubricsTree,
			basePath: rubricPath,
			meta,
			editorChoicePersons: rubricPage.blocks?.persons
		}
	};
}

export default PersonsCatalogPage;
